import { useMutation } from '@apollo/client';
import { useMelwaysInfo } from '@seek/melways-react';
import { LicenceForm, VerifiedLicenceForm } from '@seek/profile-edit-ui';
import { useEffect, type ComponentProps } from 'react';

import type {
  CredentialStatus,
  Licence as LicenceType,
  Maybe,
} from 'src/__generated__/graphql';
import { ADD_LICENCE } from 'src/components/Licences/graphql/add-licence.graphql';
import { DELETE_LICENCE } from 'src/components/Licences/graphql/delete-licence.graphql';
import { UPDATE_LICENCE } from 'src/components/Licences/graphql/update-licence.graphql';
import { useLicenceTracking } from 'src/components/Licences/hooks/tracking';
import { useConfig } from 'src/context/config';
import { logError } from 'src/modules/error-logger/browserLog';
import { getOperationNameFromQuery } from 'src/modules/utils';
import { useEditorDrawer } from 'src/ui/EditorDrawer/EditorDrawer';

type SubmitHandler = ComponentProps<typeof LicenceForm>['onSubmit'];

interface Props {
  licences: LicenceType[];
  shouldCloseDrawerOnExit?: boolean;
}

export function LicenceEditor({
  licences,
  shouldCloseDrawerOnExit = false,
}: Props) {
  const { config } = useConfig();
  const { language } = useMelwaysInfo();
  const { trackEdit, trackViewEdit, trackDelete, trackViewAdd, trackAdd } =
    useLicenceTracking();

  const { editingId, onFormDirty, onEditorClose, switchToListView } =
    useEditorDrawer();

  const defaultValues = licences.find(({ id }) => id === editingId);

  const [updateLicence] = useMutation(UPDATE_LICENCE);
  const [deleteLicence] = useMutation(DELETE_LICENCE);
  const [addLicence] = useMutation(ADD_LICENCE);

  useEffect(() => {
    if (defaultValues) {
      trackViewEdit({ licence: defaultValues });
    } else {
      trackViewAdd();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onExit = shouldCloseDrawerOnExit ? onEditorClose : switchToListView;

  const handleSubmit: SubmitHandler = async ({ values }) => {
    const mutate = defaultValues ? updateLicence : addLicence;
    try {
      await mutate({
        variables: {
          input: { id: editingId, ...values },
          languageCode: language,
          zone: config.ZONE,
        },
      });

      if (defaultValues) {
        trackEdit({ licence: defaultValues });
      } else {
        trackAdd({ licence: defaultValues });
      }

      onExit();
    } catch (err: any) {
      const mutateName = defaultValues ? 'update-licence' : 'add-licence';
      const operationName = defaultValues
        ? getOperationNameFromQuery(UPDATE_LICENCE)
        : getOperationNameFromQuery(ADD_LICENCE);
      logError({ ...err, type: mutateName }, { error: err, operationName });
    }
  };

  const handleDelete = async () => {
    try {
      await deleteLicence({
        variables: {
          input: { id: editingId },
          languageCode: language,
          zone: config.ZONE,
        },
      });
      trackDelete({ licence: defaultValues });
      onExit();
    } catch (err: any) {
      logError(
        { ...err, type: 'delete-licence' },
        {
          error: err,
          operationName: getOperationNameFromQuery(DELETE_LICENCE),
        },
      );
    }
  };

  if (defaultValues?.credential) {
    return (
      <VerifiedLicenceForm
        name={defaultValues?.name.text ?? ''}
        issuingOrganisation={defaultValues?.issuingOrganisation ?? ''}
        credentialInfo={defaultValues?.credential?.credentialInfo ?? []}
        formattedDate={defaultValues?.formattedDate ?? ''}
        status={(defaultValues?.status ?? null) as Maybe<CredentialStatus>}
        onCancel={onExit}
        onCertsyClick={onExit}
        onDelete={() => {
          const dest = defaultValues.credential?.deleteVerificationUrl;
          if (!dest) return;
          window.open(dest, '_blank', 'noopener,noreferrer');
          trackDelete({ licence: defaultValues });
        }}
        readonly
      />
    );
  }

  if (editingId && !defaultValues?.credential) {
    return (
      <LicenceForm
        locale={config.LOCALE}
        // @ts-expect-error
        initialValues={
          defaultValues
            ? {
                ...defaultValues,
                issueDate: defaultValues.issueDate || {},
                expiryDate: defaultValues.expiryDate || {},
              }
            : undefined
        }
        onCancel={onExit}
        onFormDirtyChange={onFormDirty}
        onSubmit={handleSubmit}
        onDelete={handleDelete}
        scrollToTopOnMobile={false}
      />
    );
  }

  return null;
}
